// The band that sits across every reading page in the archive.
//
// It used to appear on four pages out of nine, and to hide whatever page you
// were on, so moving between Devil Fruits, Bounties and Guides meant going
// back to the menu each time and you could never see where you were. It now
// shows every section on every one of them, marks the one you are reading, and
// sticks to the top of the page as you scroll — like the chapter rail in a
// book rather than a list you have to go back for.

import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './PageNav.css';

const BROWSE = [
  { to: '/characters', label: 'Characters' },
  { to: '/fruits', label: 'Devil Fruits' },
  { to: '/bounties', label: 'Bounties' },
  { to: '/arcs', label: 'Story Arcs' },
  { to: '/compare', label: 'Compare' },
  { to: '/guides', label: 'Guides' },
];

const PageNav = ({ current }) => {
  const location = useLocation();
  const path = current || location.pathname;
  const railRef = useRef(null);
  const [top, setTop] = useState(62);      // sits under the fixed site header
  const [height, setHeight] = useState(0); // what the spacer has to make up

  // Sticky was the wrong tool here: on a phone the band slid under the fixed
  // header and vanished, and on a laptop the main area is a flex container, so
  // the band's own box was its whole travel and it never stuck at all. Fixed
  // works in both, as long as something holds its place in the flow — hence
  // the spacer, measured rather than guessed, so it stays right when the row
  // wraps to two lines or the font size changes.
  useEffect(() => {
    const measure = () => {
      const header = document.querySelector('.universal-header');
      setTop(header ? Math.round(header.getBoundingClientRect().height) : 62);
      if (railRef.current) setHeight(Math.round(railRef.current.getBoundingClientRect().height));
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && railRef.current) observer.observe(railRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, [path]);

  // A character or an arc page belongs to its section, so the section stays lit
  // while you read one.
  const activeFor = (to) => (to === path || path.startsWith(`${to}/`));

  // The band belongs to the archive. On the games and everywhere else it only
  // took space and sat on top of the page.
  if (!BROWSE.some((item) => activeFor(item.to))) return null;

  return (
    <>
      <div className="page-nav-spacer" style={{ height }} aria-hidden="true" />
      <nav className="page-nav" ref={railRef} style={{ top }} aria-label="Archive sections">
        <div className="page-nav-rail">
          {BROWSE.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={`page-nav-link ${activeFor(item.to) ? 'is-current' : ''}`}
              aria-current={activeFor(item.to) ? 'page' : undefined}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </>
  );
};

export default PageNav;
